/**
 * ResumeMakeoverTab — Feb-2026.
 *
 * Turns the current audit into 3 downloadable DOCX resume designs
 * (Executive / Creative / Tech). Fields the audit doesn't contain (dates,
 * quantified impact, etc.) are surfaced as inline ⟨PH:label⟩ tokens
 * rendered with a soft amber highlight and an "Add your number" tag so
 * the user knows exactly what to fill in after downloading.
 *
 * Paid-plan gated at the backend (billing.enforce FEATURE_DOWNLOAD).
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import {
  FileDown,
  Loader2,
  Sparkles,
  RefreshCw,
  Wand2,
  Lock,
  AlertTriangle,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PH_RE = /⟨PH:([^⟩]+)⟩/g;

// Mirror of the backend `_placeholder_kind` classifier so the frontend
// preview shows the same context-aware suffix as the downloaded DOCX.
// Order is significant — check date before location before numeric so
// "role dates" doesn't collide with a numeric metric containing "date".
const _DATE_WORDS = ['date', 'year', 'duration', 'when', 'since', 'started', 'ended', 'from ', 'to '];
const _LOC_WORDS = ['location', 'city', 'country', 'region', 'hq', 'site', 'office', 'based', 'geo'];
const _TXT_WORDS = ['phone', 'mobile', 'email', 'linkedin', 'url', 'handle', 'name', 'username', 'degree', 'institution', 'school', 'college', 'university'];

function placeholderKind(label = '') {
  const lo = label.toLowerCase();
  if (!lo) return 'num';
  if (_DATE_WORDS.some((w) => lo.includes(w))) return 'date';
  if (_LOC_WORDS.some((w) => lo.includes(w))) return 'loc';
  if (_TXT_WORDS.some((w) => lo.includes(w))) return 'txt';
  return 'num';
}

function placeholderSuffix(label = '') {
  return { num: 'add your number', date: 'add dates', loc: 'add', txt: 'add' }[placeholderKind(label)];
}

// Sep-2026: three separate designs collapsed into ONE canonical "Classic"
// layout (per user feedback + reference PDF). The multi-card design picker
// is now a single "Download DOCX" CTA.

// Split a string on ⟨PH:…⟩ tokens; return an array of {text, placeholder}
function splitTokens(text) {
  if (!text) return [];
  const out = [];
  let last = 0;
  const re = new RegExp(PH_RE.source, 'g');
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push({ text: text.slice(last, m.index) });
    out.push({ placeholder: m[1] });
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push({ text: text.slice(last) });
  return out;
}

// Render a piece of text with amber-highlighted placeholder chips.
function Highlighted({ text, className = '' }) {
  const parts = splitTokens(text);
  if (!parts.length) return <span className={className}>{text || ''}</span>;
  return (
    <span className={className}>
      {parts.map((p, i) => {
        if (p.placeholder) {
          const suffix = placeholderSuffix(p.placeholder);
          return (
            <span
              key={i}
              title={`${suffix.charAt(0).toUpperCase()}${suffix.slice(1)} after downloading`}
              data-testid="resume-placeholder-chip"
              className="mx-0.5 inline-flex items-center gap-1 rounded-md bg-amber-100 px-1.5 py-0.5 text-[11.5px] font-medium text-amber-900 ring-1 ring-amber-200"
            >
              <AlertTriangle size={10} className="text-amber-600" />
              {p.placeholder}
              <span className="opacity-60 text-[10px]">· {suffix}</span>
            </span>
          );
        }
        return <span key={i}>{p.text}</span>;
      })}
    </span>
  );
}

// Count placeholder chips in the whole structure for the summary strip.
function countPlaceholders(structure) {
  if (!structure) return 0;
  const s = JSON.stringify(structure);
  const matches = s.match(/⟨PH:[^⟩]+⟩/g);
  return matches ? matches.length : 0;
}

export default function ResumeMakeoverTab({ analysisId, planLocked, onUpgrade }) {
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [downloadingDesign, setDownloadingDesign] = useState(null);
  const [structure, setStructure] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [error, setError] = useState(null);

  const token = () => localStorage.getItem('le_token') || '';
  const authHeaders = () => ({ Authorization: `Bearer ${token()}` });

  // Load cached resume on mount.
  useEffect(() => {
    if (!analysisId || planLocked) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/analyses/${analysisId}/resume`, {
          headers: authHeaders(),
        });
        if (cancelled) return;
        setStructure(data?.resume || null);
        setGeneratedAt(data?.generated_at || null);
      } catch (e) {
        if (e?.response?.status === 404) {
          // no resume generated yet — normal
        } else if (e?.response?.status === 402) {
          setError('locked');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [analysisId, planLocked]);

  const runGenerate = async () => {
    if (!analysisId || generating) return;
    setGenerating(true);
    setError(null);
    const toastId = toast.loading('Building your resume from the audit… ~15-30s');
    try {
      const { data } = await axios.post(
        `${API}/analyses/${analysisId}/resume/generate`,
        {},
        { headers: authHeaders(), timeout: 90000 },
      );
      setStructure(data?.resume || null);
      setGeneratedAt(new Date().toISOString());
      toast.success('Resume ready — pick a design below.', { id: toastId });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      if (status === 402) {
        setError('locked');
        toast.error('Upgrade your plan to use Resume Makeover.', { id: toastId });
      } else {
        const msg = typeof detail === 'object' ? (detail?.message || 'Resume generation failed') : (detail || 'Resume generation failed');
        toast.error(msg, { id: toastId });
      }
    } finally {
      setGenerating(false);
    }
  };

  const runDownload = async (design) => {
    if (!analysisId || downloadingDesign) return;
    setDownloadingDesign(design);
    const toastId = toast.loading(`Preparing your ${design} DOCX…`);
    try {
      const resp = await axios.get(
        `${API}/analyses/${analysisId}/resume/download?design=${encodeURIComponent(design)}`,
        {
          headers: authHeaders(),
          responseType: 'blob',
          timeout: 60000,
        },
      );
      const blob = new Blob([resp.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = resp.headers['content-disposition'] || '';
      const m = /filename="?([^";]+)"?/.exec(cd);
      a.download = m ? m[1] : `resume-${design}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Downloaded — the amber highlights show what to fill in.', { id: toastId });
    } catch (e) {
      const status = e?.response?.status;
      if (status === 402) {
        setError('locked');
        toast.error('Upgrade your plan to download resumes.', { id: toastId });
      } else {
        toast.error('Download failed — try again.', { id: toastId });
      }
    } finally {
      setDownloadingDesign(null);
    }
  };

  const placeholderCount = useMemo(() => countPlaceholders(structure), [structure]);
  const metricHints = structure?._metric_hints || [];

  // ------------- Locked (non-paid plan) view -------------
  if (planLocked || error === 'locked') {
    return (
      <div className="max-w-3xl" data-testid="resume-makeover-locked">
        <Card className="border-amber-200/60 bg-gradient-to-br from-amber-50/60 to-white">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Lock size={18} className="text-amber-600" />
              <CardTitle className="text-lg">Resume Makeover — paid plan</CardTitle>
            </div>
            <CardDescription>
              Turn this audit into three ready-to-download DOCX resumes.
              Missing metrics (revenue lift, headcount, dates) are pre-tagged in
              amber so you know exactly what to fill in after downloading.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={onUpgrade}
              className="rounded-full"
              data-testid="resume-makeover-upgrade-btn"
            >
              <Sparkles size={14} className="mr-1.5" /> Upgrade to unlock
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="resume-makeover-tab">
      {/* Hero */}
      <div className="rounded-2xl border border-white/60 dark:border-white/5 bg-white/70 dark:bg-slate-900/40 backdrop-blur-sm p-5 shadow-[0_4px_24px_-16px_rgba(120,90,200,0.25)]">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-xl font-semibold tracking-tight">Resume Makeover</h2>
            <p className="text-sm text-muted-foreground max-w-2xl mt-1">
              Auto-built from your audit's rewritten headline, about, skills, and
              experience. Missing numbers are highlighted in <span className="inline-flex items-center rounded bg-amber-100 px-1.5 text-[12px] text-amber-900 ring-1 ring-amber-200">amber</span>{' '}
              — replace them after downloading with the metrics that matter for your seat.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {structure ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={runGenerate}
                disabled={generating}
                className="rounded-full"
                data-testid="resume-regenerate-btn"
              >
                {generating ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <RefreshCw size={14} className="mr-1.5" />}
                Regenerate
              </Button>
            ) : (
              <Button
                onClick={runGenerate}
                disabled={generating || loading}
                className="rounded-full"
                data-testid="resume-generate-btn"
              >
                {generating || loading ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Wand2 size={14} className="mr-1.5" />}
                {loading ? 'Loading…' : 'Build my resume'}
              </Button>
            )}
          </div>
        </div>

        {/* Metric hints strip */}
        {structure && metricHints.length > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-1.5" data-testid="resume-metric-hints">
            <span className="text-[11px] uppercase tracking-wider text-muted-foreground">Numbers to add</span>
            {metricHints.map((m, i) => (
              <span key={i} className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-[11.5px] text-amber-900 ring-1 ring-amber-200/70">
                {m}
              </span>
            ))}
            {placeholderCount > 0 && (
              <span className="ml-1 text-[11.5px] text-muted-foreground">
                · {placeholderCount} placeholders in the draft
              </span>
            )}
          </div>
        )}
      </div>

      {!structure && !generating && !loading && (
        <div className="rounded-2xl border border-dashed border-slate-300 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-900/30 p-8 text-center">
          <Wand2 size={22} className="mx-auto text-slate-400 mb-2" />
          <p className="text-sm text-muted-foreground">
            Click <b>Build my resume</b> to draft a personalised resume from this audit — takes ~20 seconds.
          </p>
        </div>
      )}

      {structure && (
        <>
          {/* Preview card */}
          <ResumePreview structure={structure} />

          {/* Single-design download CTA — Sep-2026 collapse. */}
          <div
            className="rounded-2xl border border-slate-200/80 dark:border-slate-700/60 bg-gradient-to-br from-slate-50 to-white dark:from-slate-900 dark:to-slate-950 p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
            data-testid="resume-download-panel"
          >
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1">Classic layout</div>
              <div className="text-base font-semibold">Recruiter-ready, ATS-safe, single-column</div>
              <p className="text-[13px] text-muted-foreground mt-1 max-w-xl">
                Each section is separated by a hairline rule. Whole experience roles never split across pages. Amber highlights show exactly what to fill in after downloading.
              </p>
            </div>
            <Button
              className="rounded-full shrink-0"
              size="lg"
              onClick={() => runDownload('classic')}
              disabled={!!downloadingDesign}
              data-testid="resume-download-btn"
            >
              {downloadingDesign ? (
                <Loader2 size={14} className="mr-2 animate-spin" />
              ) : (
                <FileDown size={14} className="mr-2" />
              )}
              Download as Word (.docx)
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

// ---------------- Inline structured preview ----------------
function ResumePreview({ structure }) {
  const name = structure?.name || 'Your Name';
  const headline = structure?.headline_line || structure?.target_role || '';
  const contact = structure?.contact || {};
  const contactBits = [contact.email, contact.phone, contact.location, contact.linkedin].filter(Boolean);

  return (
    <div
      className="rounded-2xl border border-slate-200/80 dark:border-slate-700/60 bg-white dark:bg-slate-900 p-6 shadow-sm"
      data-testid="resume-preview"
    >
      <div className="border-b border-slate-200/70 pb-3 mb-3">
        <div className="text-xl font-semibold">{name}</div>
        {headline && (
          <div className="text-sm text-muted-foreground">
            <Highlighted text={headline} />
          </div>
        )}
        {contactBits.length > 0 && (
          <div className="mt-1 text-[12px] text-muted-foreground flex flex-wrap gap-x-2">
            {contactBits.map((c, i) => (
              <span key={i} className="inline-flex items-center">
                <Highlighted text={c} />
                {i < contactBits.length - 1 && <span className="ml-2 opacity-40">·</span>}
              </span>
            ))}
          </div>
        )}
      </div>

      {(structure?.recruiter_snapshot || []).length > 0 && (
        <div
          className="mt-4 rounded-xl bg-amber-50 dark:bg-amber-500/10 ring-1 ring-amber-200/70 dark:ring-amber-400/20 p-4"
          data-testid="resume-recruiter-snapshot"
        >
          <div className="text-[10px] uppercase tracking-[0.22em] text-amber-800 dark:text-amber-300 font-semibold mb-2">
            Recruiter Snapshot
          </div>
          <ul className="space-y-1.5">
            {structure.recruiter_snapshot.slice(0, 3).map((s, i) => (
              <li key={i} className="text-sm leading-relaxed text-slate-800 dark:text-slate-100 flex gap-2">
                <span className="text-amber-600 shrink-0">▸</span>
                <span className="min-w-0"><Highlighted text={s} /></span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {structure?.summary && (
        <PreviewSection title="Summary">
          <p className="text-sm leading-relaxed">
            <Highlighted text={structure.summary} />
          </p>
        </PreviewSection>
      )}

      {(structure?.core_competencies || []).length > 0 && (
        <PreviewSection title="Core Competencies">
          <div className="flex flex-wrap gap-1.5">
            {structure.core_competencies.map((c, i) => (
              <span key={i} className="rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-[12px]">
                <Highlighted text={c} />
              </span>
            ))}
          </div>
        </PreviewSection>
      )}

      {(structure?.experience || []).length > 0 && (
        <PreviewSection title="Experience">
          <div className="space-y-4">
            {structure.experience.map((role, i) => (
              <div key={i}>
                <div className="text-sm font-semibold">
                  <Highlighted text={`${role.title || ''} — ${role.company || ''}`} />
                </div>
                <div className="text-[12px] text-muted-foreground">
                  <Highlighted text={[role.dates, role.location].filter(Boolean).join(' · ')} />
                </div>
                <ul className="mt-1 list-disc pl-5 space-y-1">
                  {(role.bullets || []).map((b, bi) => (
                    <li key={bi} className="text-sm leading-relaxed">
                      <Highlighted text={b} />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </PreviewSection>
      )}

      {(structure?.education || []).length > 0 && (
        <PreviewSection title="Education">
          <ul className="space-y-1">
            {structure.education.map((e, i) => (
              <li key={i} className="text-sm">
                <Highlighted text={`${e.degree || ''} — ${e.institution || ''}`} />
                {e.dates && (
                  <span className="ml-2 text-[12px] text-muted-foreground">
                    <Highlighted text={e.dates} />
                  </span>
                )}
              </li>
            ))}
          </ul>
        </PreviewSection>
      )}

      {(structure?.certifications || []).length > 0 && (
        <PreviewSection title="Certifications">
          <ul className="list-disc pl-5 space-y-1">
            {structure.certifications.map((c, i) => (
              <li key={i} className="text-sm">
                <Highlighted text={c} />
              </li>
            ))}
          </ul>
        </PreviewSection>
      )}

      {(structure?.projects_or_awards || []).length > 0 && (
        <PreviewSection title="Selected Projects & Awards">
          <ul className="list-disc pl-5 space-y-1">
            {structure.projects_or_awards.map((a, i) => (
              <li key={i} className="text-sm">
                <Highlighted text={a} />
              </li>
            ))}
          </ul>
        </PreviewSection>
      )}
    </div>
  );
}

function PreviewSection({ title, children }) {
  return (
    <div className="mt-4 first:mt-0">
      <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-1">{title}</div>
      {children}
    </div>
  );
}
